import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Switch, Route, withRouter, Redirect } from 'react-router-dom';

import routes from 'bento-ordering/base/routes';
import modalRoutes from 'bento-ordering/base/routes/modal';
import { closeModal } from 'bento-ordering/base/actions/modal';

import { LOCATION_OFFLINE } from 'bento-ordering/locations/constants/paths';
import { shouldRedirectToOfflineSelector } from 'bento-ordering/locations/selectors';
import SetupInitialFlow from 'bento-ordering/base/containers/SetupInitialFlow';
import {
  initializeTracking,
  trackPageview
} from 'bento-ordering/base/actions/tracking';
import { setGoogleLoaded } from 'bento-ordering/base/actions/libraryLoad';
import initializeGoogle from 'bento-ordering/base/utils/initializeGoogle';

import {
  ONLINE_ORDERING_BASE_PATH,
  DINE_IN_BASE_PATH
} from 'bento-ordering/base/constants/basePaths';
import MenuManager from 'bento-ordering/menus/containers/MenuManager';

class App extends React.PureComponent {
  static propTypes = {
    location: PropTypes.object,
    dispatch: PropTypes.func,
    isModalOpen: PropTypes.bool,
    shouldRedirectToOffline: PropTypes.bool,
    account: PropTypes.object,
    menu: PropTypes.shape({ id: PropTypes.number }),
    history: PropTypes.shape({ listen: PropTypes.func }).isRequired
  };

  previousLocation = this.props.location;

  componentDidMount() {
    this.props.dispatch(initializeTracking());
    this.props.dispatch(trackPageview());
    initializeGoogle(() => {
      this.props.dispatch(setGoogleLoaded());
    });
  }

  // keep track of the location as the previous if the location
  // is not a modal location
  componentDidUpdate(prevProps) {
    const { dispatch, isModalOpen, shouldRedirectToOffline } = this.props;
    if (!isModalOpen) {
      this.previousLocation = this.props.location;
    }

    this.handleAnalytics(prevProps);

    /**
     * close all modals if user try to go to a modal route and
     * we are supposed to redirect to offline page
     */

    if (shouldRedirectToOffline) {
      dispatch(closeModal());
    }
  }

  handleAnalytics(prevProps) {
    const { location, account, dispatch } = this.props;
    if (
      location.pathname !== prevProps.location.pathname ||
      prevProps.account.name !== account.name
    ) {
      dispatch(trackPageview());
    }
  }

  renderRoute = ({ props, Layout, Component, componentProps, redirect }) => {
    const { shouldRedirectToOffline } = this.props;
    if (shouldRedirectToOffline) {
      return <Redirect to={LOCATION_OFFLINE} />;
    }
    if (redirect) {
      return (
        <Redirect exact to={redirect({ slug: props.match.params.slug })} />
      );
    }

    return (
      <React.Fragment>
        <Layout {...props} {...componentProps}>
          <Component {...props} {...componentProps} />
        </Layout>
      </React.Fragment>
    );
  };

  renderRoutes() {
    return routes.map(route => {
      return (
        <Route
          key={route.path}
          render={props =>
            this.renderRoute({
              props,
              Layout: route.Layout,
              Component: route.Component,
              componentProps: route.componentProps,
              redirect: route.redirect
            })
          }
          path={route.path}
          exact={route.exact}
        />
      );
    });
  }

  /**
   * Renders a set of routes that will render as a modal. Some
   * of these route paths will match normal normal route paths
   * because there are cases where we may have the same route, but
   * on mobile will render it as a normal page route while desktop
   * will render as a modal route.
   */
  renderModalRoutes() {
    return modalRoutes.map(route => {
      return (
        <Route
          key={route.path}
          render={props =>
            this.renderRoute({
              props,
              Layout: route.Layout,
              Component: route.Component,
              componentProps: route.componentProps
            })
          }
          path={route.path}
          exact={route.exact}
        />
      );
    });
  }
  /**
   * renders the App, including MenuManager (which controls fetching of
   * menu and other resources necessary for rendering Menu page),
   * SetupInitialFlow, and our routes (including modal routes)
   *
   * We render menu manager at the app level so that on mobile, we
   * always have a rendered menu manager that can observe changes to
   * cart fulfillment options to know when to fetch a new menu
   */
  renderApp = () => {
    const { isModalOpen } = this.props;

    return (
      <>
        <MenuManager />
        <SetupInitialFlow>
          <Switch
            location={isModalOpen ? this.previousLocation : this.props.location}
          >
            {this.renderRoutes()}
          </Switch>

          {isModalOpen && <Switch>{this.renderModalRoutes()}</Switch>}
        </SetupInitialFlow>
      </>
    );
  };

  render() {
    // block render if we are not on the a supported route
    const pathname = window.location.pathname;

    if (
      !pathname.includes(ONLINE_ORDERING_BASE_PATH) &&
      !pathname.includes(DINE_IN_BASE_PATH)
    ) {
      return null;
    }

    return this.renderApp();
  }
}

function mapStateToProps(state) {
  const { restaurantLocations, modal, account, menu } = state;
  return {
    isModalOpen: modal.isOpen,
    shouldRedirectToOffline: shouldRedirectToOfflineSelector(
      restaurantLocations
    ),
    account,
    menu
  };
}

export default withRouter(connect(mapStateToProps)(App));
